// src/v3/ui/app.tsx

import { QueryClient, QueryClientProvider, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { createBrowserRouter, RouterProvider } from "react-router-dom";
import { z } from "zod";
import { PORTFOLIO_PRICE_SYNC_EVENT, PORTFOLIO_PRICE_SYNC_STATUS, PORTFOLIO_PRICE_UPDATED_EVENT, PORTFOLIO_INSTRUMENT_KIND_OBJ,
  PORTFOLIO_PRICE_SYNC_MODE_OBJ, isPortfolioComparisonSyncStatus, isPortfolioPriceSyncActive, isRecord } from "@signals/shared";
import { AppTableProvider, Card, CardContent, CardHeader, CardTitle, RootLayout, apiFetch, qk, useEvents } from "./_common";
import { BacktesterPage } from "./backtester";
import { DatasourcePage } from "./datasource";
import { IdeasPage } from "./ideas";
import { McpSettingsPage } from "./mcp";
import { PinbarPage } from "./pinbar";
import { PortfolioPage, acceptPortfolioComparisonStatus, invalidatePortfolioComparisonAttempt } from "./portfolio";
import { SchedulerPage } from "./scheduler";
import { SymbolPage } from "./symbol";

function DashboardPage() {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Dashboard</CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-muted-foreground">
          Welcome to SignalsApp. Select a feature from the sidebar.
        </p>
      </CardContent>
    </Card>
  );
}

const router = createBrowserRouter([
  {
    path: "/",
    element: <RootLayout />,
    children: [
      { index: true, element: <DashboardPage /> },
      { path: "signals", element: <PinbarPage /> },
      { path: "symbols", element: <SymbolPage /> },
      { path: "portfolio", element: <PortfolioPage /> },
      { path: "settings/mcp", element: <McpSettingsPage /> },
      { path: "ideas", element: <IdeasPage /> },
      { path: "backtester", element: <BacktesterPage /> },
      { path: "scheduler", element: <SchedulerPage /> },
      { path: "datasource", element: <DatasourcePage /> },
    ],
  },
]);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
    },
  },
});

const portfolioPriceUpdatedBaseSchema = z.object({
  portfolioIds: z.array(z.string().uuid()),
  priceDate: z.string().date(),
});
const portfolioPriceUpdatedPayloadSchema = z.union([
  portfolioPriceUpdatedBaseSchema.extend({ symbolId: z.string().uuid(), accountId: z.string().uuid().optional(),
    instrument: z.object({ kind: z.literal(PORTFOLIO_INSTRUMENT_KIND_OBJ.SECURITY), symbolId: z.string().uuid() }).strict().optional() })
    .refine((value) => value.instrument === undefined || value.instrument.symbolId === value.symbolId),
  portfolioPriceUpdatedBaseSchema.extend({ symbolId: z.never().optional(), accountId: z.string().uuid(),
    instrument: z.object({ kind: z.literal(PORTFOLIO_INSTRUMENT_KIND_OBJ.OPTION), optionContractId: z.string().uuid() }).strict() }),
]);
const portfolioPriceSyncPayloadSchema = z.object({
  operationId: z.string(),
  portfolioId: z.string().uuid(),
  status: z.enum(PORTFOLIO_PRICE_SYNC_STATUS),
});

export function PortfolioPriceEventsHandler() {
  const queryClient = useQueryClient();
  const { events, isConnected } = useEvents([PORTFOLIO_PRICE_UPDATED_EVENT, PORTFOLIO_PRICE_SYNC_EVENT]);
  const processedCount = useRef(0);
  const activeOperations = useRef(new Map<string, {
    portfolioId: string;
    timer: ReturnType<typeof setTimeout> | null;
    inFlight: boolean;
  }>());
  const wasConnected = useRef(false);
  const hasConnected = useRef(false);
  const completedOperations = useRef(new Set<string>());

  const refreshPortfolio = (portfolioId: string, includeComparisons = true) => {
    void Promise.all([
      queryClient.invalidateQueries({ queryKey: qk.portfolio.positionsForPortfolio(portfolioId) }),
      queryClient.invalidateQueries({ queryKey: qk.portfolio.summaryForPortfolio(portfolioId) }),
      queryClient.invalidateQueries({ queryKey: qk.portfolio.snapshots(portfolioId) }),
      ...(includeComparisons ? [queryClient.invalidateQueries({ queryKey: qk.portfolio.compareForPortfolio(portfolioId) })] : []),
      queryClient.invalidateQueries({ queryKey: qk.portfolio.yearlyPerformance.all(portfolioId) }),
      queryClient.invalidateQueries({ queryKey: qk.portfolio.yearlyPerformance.singleForPortfolio(portfolioId) }),
      queryClient.invalidateQueries({ queryKey: qk.portfolio.positionHistoryForPortfolio(portfolioId) }),
    ]);
  };

  const pollOperation = (operationId: string, delayMs = 2000) => {
    const tracked = activeOperations.current.get(operationId);
    if (!tracked || tracked.timer || tracked.inFlight) return;
    tracked.timer = setTimeout(() => {
      tracked.timer = null;
      tracked.inFlight = true;
      void apiFetch<{ operationId: string; portfolioId: string; status: z.infer<typeof portfolioPriceSyncPayloadSchema>["status"] }>(
        `/api/portfolio/price-sync/${encodeURIComponent(operationId)}?portfolioId=${encodeURIComponent(tracked.portfolioId)}`,
      ).then((status) => {
        if (activeOperations.current.get(operationId) !== tracked) return;
        tracked.inFlight = false;
        if (status.portfolioId !== tracked.portfolioId || status.operationId !== operationId) {
          pollOperation(operationId);
          return;
        }
        if (isPortfolioPriceSyncActive(status.status)) {
          pollOperation(operationId);
          return;
        }
        activeOperations.current.delete(operationId);
        if (completedOperations.current.has(operationId)) return;
        completedOperations.current.add(operationId);
        refreshPortfolio(tracked.portfolioId);
      }).catch(() => {
        if (activeOperations.current.get(operationId) !== tracked) return;
        tracked.inFlight = false;
        // Keep checking after a transient network failure or while the server is reconnecting.
        pollOperation(operationId);
      });
    }, delayMs);
  };

  useEffect(() => {
    if (isConnected && !wasConnected.current) {
      const activePortfolioIds = new Set<string>();
      const comparisons = new Map<string, { portfolioId: string; from: string; to: string }>();
      for (const query of queryClient.getQueryCache().findAll({ type: "active" })) {
        const key = query.queryKey;
        if (key[0] !== "portfolio") continue;
        const params = key[2];
        if (key[1] === qk.portfolio.compareForPortfolio("")[1]) {
          if (isRecord(params) && typeof params["portfolioId"] === "string" && typeof params["from"] === "string" && typeof params["to"] === "string") {
            const scope = { portfolioId: params["portfolioId"], from: params["from"], to: params["to"] };
            comparisons.set(JSON.stringify(scope), scope);
          }
          continue;
        }
        if (key[1] === qk.portfolio.comparisonOperation("", "", "", "")[1]) continue;
        const portfolioId = isRecord(params) && typeof params["portfolioId"] === "string" ? params["portfolioId"]
          : typeof params === "string" && key[1] !== "yearly-performance" && !(key[1] === "broker-sync" && params === qk.portfolio.brokerSync.settings[2]) ? params
          : key[1] === "yearly-performance" && params === qk.portfolio.yearlyPerformance.all("")[2] && typeof key[3] === "string" ? key[3] : null;
        if (portfolioId) activePortfolioIds.add(portfolioId);
      }
      for (const portfolioId of activePortfolioIds) refreshPortfolio(portfolioId, ![...comparisons.values()].some((scope) => scope.portfolioId === portfolioId));
      if (hasConnected.current) for (const scope of comparisons.values()) {
        const key = qk.portfolio.compare(scope.portfolioId, scope.from, scope.to);
        const comparison = queryClient.getQueryData<unknown>(key);
        const preparation = isRecord(comparison) && isRecord(comparison["preparation"]) ? comparison["preparation"]["operation"] : null;
        if (isPortfolioComparisonSyncStatus(preparation) && preparation.portfolioId === scope.portfolioId && preparation.from === scope.from && preparation.to === scope.to) {
          const statusKey = qk.portfolio.comparisonOperation(scope.portfolioId, scope.from, scope.to, preparation.operationId);
          const cached = queryClient.getQueryData<unknown>(statusKey);
          const status = isPortfolioComparisonSyncStatus(cached) && cached.attempt >= preparation.attempt ? cached : preparation;
          if (isPortfolioPriceSyncActive(status.status)) {
            if (queryClient.getQueryState(statusKey)?.fetchStatus !== "fetching") void queryClient.invalidateQueries({ queryKey: statusKey, exact: true });
            continue;
          }
        }
        if (queryClient.getQueryState(key)?.fetchStatus !== "fetching") void queryClient.invalidateQueries({ queryKey: key, exact: true });
      }
      hasConnected.current = true;
      for (const [operationId, operation] of activeOperations.current) {
        if (operation.timer) clearTimeout(operation.timer);
        operation.timer = null;
        pollOperation(operationId, 0);
      }
    }
    wasConnected.current = isConnected;

    const newEvents = events.slice(processedCount.current);
    processedCount.current = events.length;

    for (const event of newEvents) {
      if (event.type === PORTFOLIO_PRICE_SYNC_EVENT) {
        if (isRecord(event.data) && event.data["mode"] === PORTFOLIO_PRICE_SYNC_MODE_OBJ.COMPARISON) {
          if (isPortfolioComparisonSyncStatus(event.data) && acceptPortfolioComparisonStatus(queryClient, event.data)) invalidatePortfolioComparisonAttempt(queryClient, event.data);
          continue;
        }
        const parsed = portfolioPriceSyncPayloadSchema.safeParse(event.data);
        if (!parsed.success) continue;
        const portfolioId = parsed.data.portfolioId;
        if (completedOperations.current.has(parsed.data.operationId)) continue;
        if (isPortfolioPriceSyncActive(parsed.data.status)) {
          const prior = activeOperations.current.get(parsed.data.operationId);
          if (prior) continue;
          activeOperations.current.set(parsed.data.operationId, { portfolioId, timer: null, inFlight: false });
          pollOperation(parsed.data.operationId);
        } else {
          const prior = activeOperations.current.get(parsed.data.operationId);
          if (prior?.timer) clearTimeout(prior.timer);
          activeOperations.current.delete(parsed.data.operationId);
          completedOperations.current.add(parsed.data.operationId);
          refreshPortfolio(portfolioId);
        }
        continue;
      }
      if (event.type !== PORTFOLIO_PRICE_UPDATED_EVENT) continue;
      const parsed = portfolioPriceUpdatedPayloadSchema.safeParse(event.data);
      if (!parsed.success) continue;

      for (const portfolioId of new Set(parsed.data.portfolioIds)) {
        refreshPortfolio(portfolioId);
      }
    }
  }, [events, isConnected, queryClient]);

  useEffect(() => () => {
    for (const operation of activeOperations.current.values()) {
      if (operation.timer) clearTimeout(operation.timer);
    }
    activeOperations.current.clear();
  }, []);

  return null;
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <PortfolioPriceEventsHandler />
      <AppTableProvider>
        <RouterProvider router={router} />
      </AppTableProvider>
    </QueryClientProvider>
  );
}
